import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authClient } from "@/lib/auth";
import { toast } from "sonner";

const UnlockSchema = z.object({
  password: z.string().optional(),
  pin: z
    .string()
    .optional()
    .refine((v) => !v || /^\d{6}$/.test(v), { message: "6 digits" }),
});

export function MobileLockOverlay({ onUnlock }: { onUnlock: () => void }) {
  const pinStatus = useQuery<{ hasLockPin: boolean }>({
    queryKey: ["/api/me/lock-pin"],
    queryFn: async () => {
      const r = await fetch("/api/me/lock-pin", { credentials: "include" });
      if (!r.ok) return { hasLockPin: false };
      return r.json();
    },
    staleTime: 5 * 60 * 1000,
  });
  // Optimistically show the PIN field until we confirm the user has
  // no PIN — avoids the input being missing during the initial
  // pin-status fetch.
  const hasPin = pinStatus.isSuccess ? pinStatus.data.hasLockPin : true;

  const { register, handleSubmit, setValue, watch } = useForm<z.infer<typeof UnlockSchema>>({
    resolver: zodResolver(UnlockSchema),
  });
  const pinValue = watch("pin") ?? "";
  const [busy, setBusy] = useState(false);

  async function onSubmit(d: z.infer<typeof UnlockSchema>) {
    if (!d.password && !d.pin) {
      toast.error("Enter password or PIN");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch("/api/session/unlock", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...(d.password ? { password: d.password } : {}),
          ...(d.pin ? { pin: d.pin } : {}),
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        toast.error(body.message ?? "Unlock failed");
        return;
      }
      onUnlock();
    } finally {
      setBusy(false);
    }
  }

  async function tryPasskey() {
    // Routes through Better Auth's signIn.passkey — performs the
    // WebAuthn assertion + creates a fresh authenticated session
    // (replacing the locked one). See LockOverlay.tsx for the
    // security rationale for replacing the prior unlock-passkey path.
    try {
      const res = await authClient.signIn.passkey();
      if (res?.error) throw new Error(res.error.message ?? "Passkey failed");
      onUnlock();
    } catch (e) {
      toast.error((e as Error).message);
    }
  }
  return (
    <div className="fixed inset-0 z-[2000] flex items-end bg-background">
      <div className="w-full rounded-t-[20px] border-t border-border bg-card p-5 shadow-lg">
        <h2 className="font-display text-xl font-bold">Locked</h2>
        <p className="mb-4 text-xs text-muted-foreground">Unlock to continue.</p>
        <Button variant="outline" className="mb-3 h-12 w-full" onClick={tryPasskey}>
          Use Face ID / passkey
        </Button>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          {hasPin && (
            <div className="space-y-1">
              <Label htmlFor="pin">PIN</Label>
              <Input
                id="pin"
                inputMode="numeric"
                pattern="\d*"
                maxLength={6}
                autoComplete="off"
                placeholder="••••••"
                className="h-12 text-base"
                value={pinValue}
                onChange={(e) => {
                  // Strip non-digits, cap at 6, auto-submit when full.
                  const digits = e.target.value.replace(/\D/g, "").slice(0, 6);
                  setValue("pin", digits, { shouldValidate: true });
                  if (digits.length === 6 && !busy) {
                    void handleSubmit(onSubmit)();
                  }
                }}
              />
            </div>
          )}
          <div className="space-y-1">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              className="h-12 text-base"
              {...register("password")}
            />
          </div>
          <Button type="submit" className="h-12 w-full" disabled={busy}>
            {busy ? "Unlocking…" : "Unlock"}
          </Button>
        </form>
      </div>
    </div>
  );
}
